import {FeatureCollection, MultiPolygon} from 'geojson';
import {Parcel} from '@/domain/entities/Parcel';

import type {GeoJSONSourceSpecification, LngLatBoundsLike} from 'maplibre-gl';
import {LayerSpecification} from 'maplibre-gl';

import {bbox} from '@turf/bbox';
import {Dispute} from '@/domain/entities/Dispute';
import {PROJECT_OVERLAYS_ID} from '@/config';

type ParcelProperties = {
  uuid: string;
  polygon: number;
  parcel: number;
  subparcel?: number;
  city?: string;
  distance?: number;
  ownerId?: number;
  projectId: string;
};

export const getFeatureCollection = (parcels?: Array<Parcel>): FeatureCollection<MultiPolygon, ParcelProperties> => ({
  type: 'FeatureCollection',
  features: parcels
    ? parcels.map(parcel => ({
      type: 'Feature',
      geometry: parcel.geom,
      properties: {
        uuid: parcel.uuid,
        polygon: parcel.polygon,
        parcel: parcel.parcel,
        subparcel: parcel.subparcel,
        city: parcel.city,
        distance: parcel.distance,
        ownerId: parcel.ownerId,
        projectId: parcel.projectId
      },
    }))
    : [],
});

export const parcelsSource = (parcels?: Array<Parcel>): { parcels: GeoJSONSourceSpecification } => {
  const featureCollection = getFeatureCollection(parcels) as FeatureCollection<MultiPolygon>;
  return {
    parcels: {
      type: 'geojson',
      data: featureCollection
    }
  };
};

export const disputesOverlaySource = (disputesOverlay?: FeatureCollection<MultiPolygon, Omit<Dispute, 'geom'>>): { disputes: GeoJSONSourceSpecification } => {
  return {
    disputes: {
      type: 'geojson',
      data: disputesOverlay ?? {
        type: 'FeatureCollection',
        features: []
      }
    }
  };
};

export const sourcesConstructor = (
  overlayIds: Array<string>,
  parcels?: Array<Parcel>,
  disputesOverlay?: FeatureCollection<MultiPolygon, Omit<Dispute, 'geom'>>
) => {
  if(overlayIds.includes(PROJECT_OVERLAYS_ID.DISPUTES)){
    return {
      ...parcelsSource(parcels),
      ...disputesOverlaySource(disputesOverlay)
    };
  } else {
    return parcelsSource(parcels);
  }
};

export const parcelLayers = (parcelFill: boolean, userId?: number, selectedParcelUuid?: string): Array<LayerSpecification> => {
  return [
    {
      'id': 'others-fill',
      'type': 'fill',
      'filter': [ 'all',
        userId ? ['!=', ['get', 'ownerId'], userId] : true,
        selectedParcelUuid ? ['!=', ['get', 'uuid'], selectedParcelUuid] : true
      ],
      'source': 'parcels',
      'paint': {
        'fill-color': '#f5017a',
        'fill-opacity': parcelFill ? 0.3 : 0
      }
    },
    {
      'id': 'others-line',
      'type': 'line',
      'filter': [ 'all',
        userId ? ['!=', ['get', 'ownerId'], userId] : true,
        selectedParcelUuid ? ['!=', ['get', 'uuid'], selectedParcelUuid] : true
      ],
      'source': 'parcels',
      'paint': {
        'line-color': '#f5017a',
        'line-opacity': 1
      }
    },
    {
      'id': 'user-fill',
      'type': 'fill',
      'filter': [ 'all', 
        userId ? ['==', ['get', 'ownerId'], userId] : true,
        selectedParcelUuid ? ['!=', ['get', 'uuid'], selectedParcelUuid] : true
      ],
      'source': 'parcels',
      'paint': {
        'fill-color': '#00ffff',
        'fill-opacity': parcelFill ? 0.3 : 0
      }
    },
    {
      'id': 'user-line',
      'type': 'line',
      'filter': [ 'all',
        userId ? ['==', ['get', 'ownerId'], userId] : true,
        selectedParcelUuid ? ['!=', ['get', 'uuid'], selectedParcelUuid] : true
      ],
      'source': 'parcels',
      'paint': {
        'line-color': '#00ffff',
        'line-opacity': 1,
        'line-width': 2
      }
    },
    {
      'id': 'selected-line',
      'type': 'line',
      'filter': selectedParcelUuid ? ['==', ['get', 'uuid'], selectedParcelUuid] : ['==', '1', '2'],
      'source': 'parcels',
      'paint': {
        'line-color': '#f1e70a',
        'line-opacity': 1,
        'line-width': 5
      }
    },
  ];
};

export const disputesLayer = (): Array<LayerSpecification> => {
  return [
    {
      'id': 'fill',
      'type': 'fill',
      'source': 'disputes',
      'paint': {
        'fill-color': '#0173f5',
        'fill-opacity': 0.3
      }
    },
    {
      'id': 'line',
      'type': 'line',
      'source': 'disputes',
      'paint': {
        'line-color': '#0173f5',
        'line-opacity': 1
      }
    }
  ];
};

export const getFeatureCollectionBounds = (parcels: Array<Parcel>): LngLatBoundsLike | undefined => {
  if(!parcels || parcels.length === 0) return undefined;
  const featureCollection = getFeatureCollection(parcels);
  const bboxCoords = bbox(featureCollection);
  return [[bboxCoords[0], bboxCoords[1]], [bboxCoords[2], bboxCoords[3]]];
};

export const getParcelBounds = (parcel: Parcel): LngLatBoundsLike | undefined => {
  if(!parcel) return undefined;
  const featureCollection = getFeatureCollection([parcel]);
  const bboxCoords = bbox(featureCollection);
  return [[bboxCoords[0], bboxCoords[1]], [bboxCoords[2], bboxCoords[3]]];
};
